/*
  Copyright (c) 2018-present, Accuracy.
  All rights reserved.
*/
import {
  createStyles, Dialog,
  GridList,
  GridListTile,
  Slide,
  StyleRules,
  Theme,
  useMediaQuery
} from "@material-ui/core";
import makeStyles from '@material-ui/core/styles/makeStyles';
import {TransitionProps} from "@material-ui/core/transitions";
import classNames from 'classnames';
import React, {memo, useEffect, useRef, useState} from 'react';
import {useTranslation} from "react-i18next";
import {asset} from "../../utils/asset";

//----------------------------------------------------------------------------------------------------------------------
//- Styles
//----------------------------------------------------------------------------------------------------------------------
export type GalleryClassKey = 'root' | 'gridList' | 'mobileList' | 'mobileImg' | 'img' | 'viewer' | 'viewerTop' | 'viewerCounter' | 'viewerClose' | 'viewerStage' | 'viewerImg' | 'viewerBottom' | 'viewerNav' | 'viewerDivider';

const useStyles = makeStyles<Theme, {}, GalleryClassKey>(
  (): StyleRules<GalleryClassKey> =>
    createStyles({
      root: {},
      gridList: {
        width: 'calc(100% - 2 * clamp(20px, 4vw, 56px))',
        // GridList sets `style={{margin: -spacing / 2}}` inline on its root
        // (see @material-ui/core/GridList/GridList.js) — an inline style
        // always wins over a plain class rule, so without !important this
        // gets clobbered back to that -2px inline margin, which (with no
        // `auto` to balance the reduced width) hugs the left and leaves the
        // freed-up space as a gap on the right instead of centering.
        margin: '0 auto !important',
        // MUI's GridList defaults to overflowY: 'auto', which (combined with a
        // fixed height) used to trap photos in their own internal scrollbar —
        // they never scrolled with the page, so they never passed under the
        // blurred TopBar/tabsWrapper. Let the grid grow naturally instead.
        overflowY: 'visible'
      },
      // Mobile bypasses GridList/GridListTile entirely (see the render
      // branch below) rather than reusing them with cols=1: GridListTile's
      // built-in "cover" fit — the imgFullWidth/imgFullHeight classes it
      // toggles via JS to crop an image to a *fixed* box — assumes a
      // determinate cellHeight. With cellHeight="auto" that box has no
      // real height for the JS to measure against, so instead of each
      // image settling at its own native ratio, every tile ends up
      // sized off whatever stale/indeterminate measurement the "cover"
      // logic produces — an actual aspect-ratio bug, not just a cosmetic
      // one. A plain stack of `<img>` elements has no such logic to
      // fight: an img with only `width` set keeps its ratio natively.
      mobileList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        width: 'calc(100% - 2 * clamp(20px, 4vw, 56px))',
        margin: '0 auto'
      },
      mobileImg: {
        display: 'block',
        width: '100%',
        height: 'auto',
        cursor: 'pointer'
      },
      img: {
        pointerEvents: 'none'
      },
      viewer: {
        position: 'fixed',
        inset: 0,
        background: '#000',
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
      },
      viewerTop: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '18px clamp(16px, 3vw, 32px)',
        fontFamily: 'var(--font-body)',
        fontSize: '11px',
        letterSpacing: '.16em',
        textTransform: 'uppercase',
        color: 'var(--color-text-dim)'
      },
      viewerCounter: {},
      viewerClose: {
        background: 'none',
        border: '1px solid var(--color-border-soft)',
        color: 'rgba(232,230,226,.7)',
        padding: '8px 16px',
        cursor: 'pointer',
        fontFamily: 'var(--font-body)',
        fontSize: '11px',
        letterSpacing: '.16em',
        textTransform: 'uppercase',
        '&:hover': {
          borderColor: 'var(--color-text-strong)',
          color: 'var(--color-text-strong)'
        }
      },
      viewerStage: {
        flex: 1,
        minHeight: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 clamp(12px, 3vw, 40px)'
      },
      viewerImg: {
        maxWidth: '100%',
        maxHeight: '100%',
        objectFit: 'contain'
      },
      viewerBottom: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '26px',
        padding: '20px clamp(16px, 3vw, 32px) 30px'
      },
      viewerNav: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: 'var(--color-text-dim)',
        fontFamily: 'var(--font-body)',
        fontSize: '11px',
        letterSpacing: '.18em',
        textTransform: 'uppercase',
        '&:hover': {
          color: 'var(--color-text-strong)'
        }
      },
      viewerDivider: {
        width: '1px',
        height: '14px',
        background: 'var(--color-border-soft)'
      }
    }),
  {
    name: 'Gallery'
  });

//----------------------------------------------------------------------------------------------------------------------
//- Component
//----------------------------------------------------------------------------------------------------------------------
export type GalleryProps = {
  className?: string;
  tileData: {img: string, title: string, cols?: number}[];
};

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default memo(({className, tileData}: GalleryProps) => {
  // hooks
  const classes = useStyles({});
  const {t} = useTranslation('common');
  const [dialog, setDialog] = useState<{open: boolean, index: number}>({open: false, index: 0});
  // The GridList's `cols`/`cellHeight` drive actual layout math (column
  // widths, row heights) rather than pure CSS, so they need a JS breakpoint
  // rather than a media query. Below 700px, GridList/GridListTile aren't
  // used at all — see the mobileList render branch below — so cols/
  // cellHeight here only matter for the tablet/desktop GridList branch.
  const isMobile = useMediaQuery('(max-width:700px)');
  const isTablet = useMediaQuery('(max-width:1024px)');
  const cols = isTablet ? 3 : 5;
  const cellHeight = isTablet ? 220 : 285;

  const count = tileData.length;

  const handleClick = (index: number) => () => {
    setDialog({open: true, index});
  };
  const handleClose = () => {
    setDialog((d) => ({...d, open: false}));
  };
  const step = (delta: number) => {
    setDialog((d) => ({...d, index: (d.index + delta + count) % count}));
  };
  const handlePrev = () => step(-1);
  const handleNext = () => step(1);

  useEffect(() => {
    if (!dialog.open) {
      return;
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      } else if (e.key === 'ArrowRight') {
        step(1);
      } else if (e.key === 'ArrowLeft') {
        step(-1);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialog.open, count]);

  // MUI's Modal locks background scroll by setting `body{overflow:hidden}`,
  // which most browsers honour but mobile Safari famously doesn't — it
  // still lets a touch-drag pan/rubber-band the page behind a fixed-position
  // overlay, which is exactly the "can still scroll down and see [the grid],
  // recovers after [closing]" bug. Pinning the body in place with a negative
  // top offset (and restoring the scroll position on close) is the standard
  // workaround, since iOS does respect `position:fixed`.
  useEffect(() => {
    if (!dialog.open) {
      return;
    }
    const scrollY = window.scrollY;
    const {body} = document;
    const prev = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width
    };
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    return () => {
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.left = prev.left;
      body.style.right = prev.right;
      body.style.width = prev.width;
      window.scrollTo(0, scrollY);
    };
  }, [dialog.open]);

  // Touch swipe left/right through the fullscreen viewer, alongside the
  // existing prev/next buttons. Tracked with a ref (not state) since only
  // the end handler needs the start point, and it shouldn't trigger renders.
  const touchStartRef = useRef<{x: number, y: number} | null>(null);
  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    touchStartRef.current = {x: touch.clientX, y: touch.clientY};
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (!start) {
      return;
    }
    const touch = e.changedTouches[0];
    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    const SWIPE_THRESHOLD = 50;
    if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
      if (dx < 0) {
        handleNext();
      } else {
        handlePrev();
      }
    }
  };

  const current = tileData[dialog.index];

  return (
    <div className={classNames(classes.root, className)}>
      {isMobile ? (
        <div className={classes.mobileList}>
          {tileData.map((tile, index) => (
            <img
              key={tile.img}
              className={classes.mobileImg}
              src={asset(tile.img)}
              alt={tile.title}
              onClick={handleClick(index)}
            />
          ))}
        </div>
      ) : (
        <GridList cellHeight={cellHeight} className={classes.gridList} cols={cols}>
          {tileData.map((tile, index) => (
            <GridListTile key={tile.img} cols={tile?.cols || 1} onClick={handleClick(index)}>
              <img className={classes.img} src={asset(tile.img)} alt={tile.title} />
            </GridListTile>
          ))}
        </GridList>
      )}
      <Dialog fullScreen open={dialog.open} onClose={handleClose} TransitionComponent={Transition}>
        {current && (
          <div className={classes.viewer}>
            <div className={classes.viewerTop}>
              <span className={classes.viewerCounter}>{String(dialog.index + 1).padStart(2, '0')} / {String(count).padStart(2, '0')}</span>
              <button className={classes.viewerClose} onClick={handleClose}>{t('gallery.close')}</button>
            </div>
            <div className={classes.viewerStage} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
              <img className={classes.viewerImg} src={asset(current.img)} alt={current.title} />
            </div>
            <div className={classes.viewerBottom}>
              <button className={classes.viewerNav} onClick={handlePrev}>← {t('gallery.prev')}</button>
              <span className={classes.viewerDivider} />
              <button className={classes.viewerNav} onClick={handleNext}>{t('gallery.next')} →</button>
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
});
